import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Input, InputGroup, InputGroupAddon } from "reactstrap";
import { Link } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import * as Loading from "@repoflow/components/loading";
import * as BucketQL from "queries/bucket";
import * as Section from "@repoflow/components/section";

// Search on SUBMIT, not on every keystroke: type freely, press Enter or hit the
// button, and the box holds with a spinner until the results land.
//
// This component used to carry a warm-up state as well — three seconds of
// "Waking up search…" on first interaction, an arm-once ref, a React-owned timer
// and a StrictMode hazard in the middle of it. All of that existed to hide the
// 13-15s an OpenSearch Serverless collection took to resume from idle. Search is
// now an index shard read from S3, so the first search costs the same as the
// second and there is nothing left to hide.
//
// Submit-only is kept. Now that a query is a couple of hundred milliseconds,
// search-as-you-type is a real option — but it is a change to how the box
// behaves rather than to what is behind it, and worth making on its own.
export const SearchBox = ({ onResults }) => {
  const [term, setTerm] = useState("");
  const inputRef = useRef(null);

  const [runSearch, { loading }] = useLazyQuery(BucketQL.search, {
    fetchPolicy: "no-cache",
    onCompleted: (data) =>
      onResults(data?.viewer?.account?.buckets?.search?.items ?? []),
    // A failed search must not leave the box disabled forever.
    onError: () => onResults([]),
  });

  const disabled = loading;

  // Disabling the input drops focus, so hand it back when it re-enables after a
  // search. Without this the user has to click again to type their next query.
  //
  // Gated on the disabled -> enabled transition rather than on `!disabled`: the
  // box is enabled on mount too, and the plain check stole the caret on page
  // load from a user who never asked for it. StrictMode double-invokes this
  // effect, but both passes store the same value, so the write is safe.
  const prevDisabledRef = useRef(false);
  useEffect(() => {
    if (prevDisabledRef.current && !disabled && inputRef.current) {
      inputRef.current.focus();
    }
    prevDisabledRef.current = disabled;
  }, [disabled]);

  const onSubmit = (event) => {
    event.preventDefault();
    const query = term.trim();

    // An empty box is not a search — it is "show me the ordinary list again".
    if (!query) {
      onResults(null);
      return;
    }

    runSearch({ variables: { input: { query, limit: 5 } } });
  };

  return (
    <Form onSubmit={onSubmit}>
      <InputGroup>
        <Input
          innerRef={inputRef}
          value={term}
          disabled={disabled}
          onChange={(event) => setTerm(event.target.value)}
          placeholder={loading ? "Searching…" : "Search buckets"}
          aria-busy={disabled}
        />
        <InputGroupAddon addonType="append">
          <Button color="primary" type="submit" disabled={disabled}>
            {disabled ? <Loading.Icon /> : <i className="fa fa-search" />}
          </Button>
        </InputGroupAddon>
      </InputGroup>
    </Form>
  );
};

// Results are rendered here rather than through @repoflow/components/list-cursor:
// that component folds orderBy/orderMode into its `input`, and SearchInput takes
// neither — results come back in score order.
export const SearchResults = ({ items }) => {
  const { base } = Section.useSectionContext();

  if (items.length === 0) {
    return <Alert color="light">No buckets match that search.</Alert>;
  }

  return (
    <table className="table">
      <tbody>
        {items.map(({ bucketId, description }) => (
          <tr key={bucketId}>
            <td>{description}</td>
            <td className="text-muted">{bucketId}</td>
            <td className="text-right">
              <Link className="btn btn-primary btn-sm" to={`${base}/${bucketId}/`}>
                View
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
